import {
    acknowledgeConflicts,
    answerSlot,
    applyMutation,
    findingPhotoSlot,
    newUlid,
    recordUnreadablePhotos,
    requireTenantDb,
    retryPhotoUpload,
    type PhotoRecord,
    type TenantDatabase,
    type WireScope,
} from '@/shared/offline';

import { assertEditable } from '@/entities/inspection';

import { photoFileName, photoMimeType } from './files';
import { shrinkPhoto } from './shrink';

export interface AddPhotosInput {
    inspectionUuid: string;
    questionId: number;
    multiScope: WireScope;
    files: readonly File[];
    /** "dakvlak" - het bijschrift krijgt een volgnummer: "dakvlak 2". */
    captionBase: string;
    /** Het aantal foto's dat al bij deze vraag staat. */
    existingCount: number;
}

export interface AddFindingPhotosInput {
    inspectionUuid: string;
    /** De handmatige bevinding; mag nog niet opgeslagen zijn - de foto wacht dan op haar. */
    findingUuid: string;
    files: readonly File[];
    /** "voegwerk" - het bijschrift krijgt een volgnummer: "voegwerk 2". */
    captionBase: string;
    existingCount: number;
}

interface PhotoBytes {
    blob: Blob;
    mimeType: string;
    fileName: string;
}

interface ReadFiles {
    photos: PhotoBytes[];
    /** Wat op het laatste moment toch niet te lezen bleek; komt in de conflictenlijst. */
    unreadable: string[];
}

/**
 * De bytes worden vóór de transactie gelezen. Een `await` op iets anders dan
 * Dexie binnen de transactie laat die voortijdig committen, en een File uit een
 * `<input>` of een drop is niet overal te klonen naar IndexedDB.
 *
 * Eén bestand tegelijk, en een bestand dat niet gelezen kan worden houdt de rest
 * niet tegen:
 *
 * - `Promise.all` las twintig telefoonfoto's tegelijk in het geheugen en liet bij
 *   één leesfout de hele selectie vallen - de inspecteur hield dan niets over.
 * - Wat toch niet te lezen is, verdwijnt niet stil: het staat in de
 *   conflictenlijst boven de vragen.
 *
 * Elke foto wordt hier ook verkleind (`shrinkPhoto`): wat in de wachtrij staat
 * is wat over de lijn gaat, en dat is een tiende van een telefoonfoto.
 */
async function readFiles(files: readonly File[]): Promise<ReadFiles> {
    const result: ReadFiles = { photos: [], unreadable: [] };

    for (const file of files) {
        try {
            const bytes = new Uint8Array(await file.arrayBuffer());

            if (bytes.byteLength === 0) {
                throw new Error('leeg bestand');
            }

            const mimeType = photoMimeType(file, bytes);

            result.photos.push(
                await shrinkPhoto(
                    new Blob([bytes], { type: mimeType }),
                    mimeType,
                    photoFileName(file),
                ),
            );
        } catch {
            result.unreadable.push(photoFileName(file));
        }
    }

    return result;
}

function photoRecords(
    input: { inspectionUuid: string; captionBase: string; existingCount: number },
    photos: readonly PhotoBytes[],
    fixed: Pick<PhotoRecord, 'questionId' | 'multiScope' | 'slot' | 'findingUuid'>,
    clientUpdatedAt: string,
    base: number,
): PhotoRecord[] {
    return photos.map((photo, index) => ({
        ...fixed,
        clientUuid: newUlid(base + index),
        inspectionUuid: input.inspectionUuid,
        answerUuid: null,
        blob: photo.blob,
        mimeType: photo.mimeType,
        fileName: photo.fileName,
        byteSize: photo.blob.size,
        caption: `${input.captionBase} ${input.existingCount + index + 1}`.trim(),
        takenAt: clientUpdatedAt,
        state: 'pending',
        attempts: 0,
        nextAttemptAt: 0,
        lastError: null,
        urls: null,
        createdAt: base + index,
        mediaUuid: null,
        deleted: 0,
    }));
}

/**
 * Een foto hoort bij een vraag, niet bij een dossier - of bij een bevinding die
 * de inspecteur zelf toevoegde. Hij wordt lokaal als Blob bewaard en gaat als
 * laatste naar de server (`POST /media`), pas als het antwoord of de bevinding
 * op de server staat - zie `shared/offline/photo-upload`.
 */
export const photoMutations = {
    /** Eén of meer foto's tegelijk, in de volgorde van de selectie of de drop. */
    async addToQuestion(input: AddPhotosInput, db: TenantDatabase = requireTenantDb()) {
        if (input.files.length === 0) {
            return [];
        }

        const { photos, unreadable } = await readFiles(input.files);
        const slot = answerSlot(input.questionId, input.multiScope);
        const base = Date.now();

        const queued = await applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    await assertEditable(database, input.inspectionUuid);

                    await database.photos.bulkPut(
                        photoRecords(
                            input,
                            photos,
                            {
                                questionId: input.questionId,
                                multiScope: [...input.multiScope],
                                slot,
                                findingUuid: null,
                            },
                            clientUpdatedAt,
                            base,
                        ),
                    );
                },
            },
            db,
        );

        await recordUnreadablePhotos(db, input.inspectionUuid, unreadable, base);

        return queued;
    },

    /**
     * Foto's bij een handmatige bevinding. Ze gaan naar de server zodra de
     * bevinding daar staat (`mediable_type: finding`).
     */
    async addToFinding(input: AddFindingPhotosInput, db: TenantDatabase = requireTenantDb()) {
        if (input.files.length === 0) {
            return [];
        }

        const { photos, unreadable } = await readFiles(input.files);
        const base = Date.now();

        const queued = await applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    await assertEditable(database, input.inspectionUuid);

                    await database.photos.bulkPut(
                        photoRecords(
                            input,
                            photos,
                            {
                                questionId: 0,
                                multiScope: [],
                                slot: findingPhotoSlot(input.findingUuid),
                                findingUuid: input.findingUuid,
                            },
                            clientUpdatedAt,
                            base,
                        ),
                    );
                },
            },
            db,
        );

        await recordUnreadablePhotos(db, input.inspectionUuid, unreadable, base);

        return queued;
    },

    /**
     * De foto's van een bevinding die nooit werd opgeslagen, of die net is
     * verwijderd: lokaal weg. Wat al op de server stond, verwijdert de server met
     * de bevinding, en dat komt als `delete` terug in de pull.
     */
    async discardForFinding(
        input: { inspectionUuid: string; findingUuid: string },
        db: TenantDatabase = requireTenantDb(),
    ) {
        return applyMutation(
            {
                apply: async (database) => {
                    await database.photos
                        .where('[inspectionUuid+slot]')
                        .equals([input.inspectionUuid, findingPhotoSlot(input.findingUuid)])
                        .delete();
                },
            },
            db,
        );
    },

    /**
     * Een foto nog een keer aanbieden: een die de server weigerde, of een die na
     * netwerkfouten op zijn volgende poging wacht. De bytes staan er nog, dus de
     * inspecteur hoeft niets opnieuw te fotograferen; de melding in de
     * conflictenlijst verdwijnt, en de sync-engine pakt hem meteen op.
     *
     * Ook na het indienen: de foto hoort bij de opname zoals die werd ingediend,
     * en de server neemt hem dan nog aan (het concept wordt erop herbouwd).
     */
    async retry(clientUuid: string, db: TenantDatabase = requireTenantDb()) {
        const queued = await applyMutation(
            {
                apply: async (database) => {
                    const photo = await database.photos.get(clientUuid);

                    if (!photo) {
                        return;
                    }

                    await retryPhotoUpload(database, photo);
                },
            },
            db,
        );

        await acknowledgeConflicts(db, [clientUuid, `${clientUuid}:delete`]);

        return queued;
    },

    /**
     * "Overslaan": een foto die niet naar de server komt, gaat niet meer mee.
     * Alleen voor een foto die er nog niet staat; ook na het indienen, want aan
     * de opname op de server verandert er niets. Een upload die misschien al
     * aankwam, wordt daarna op de server verwijderd.
     */
    async skipUpload(clientUuid: string, db: TenantDatabase = requireTenantDb()) {
        const queued = await applyMutation(
            {
                apply: async (database) => {
                    const photo = await database.photos.get(clientUuid);

                    if (!photo || photo.state === 'uploaded') {
                        return;
                    }

                    if (photo.state === 'uploading') {
                        await database.photos.update(clientUuid, {
                            deleted: 1,
                            attempts: 0,
                            nextAttemptAt: 0,
                            lastError: null,
                        });

                        return;
                    }

                    await database.photos.delete(clientUuid);
                },
            },
            db,
        );

        await acknowledgeConflicts(db, [clientUuid]);

        return queued;
    },

    /**
     * Verwijdert een foto van een vraag.
     *
     * - Nog niet op de server (wacht op zijn antwoord, of geweigerd): meteen weg.
     * - Op de server, of nog onderweg: hij verdwijnt meteen uit beeld en de
     *   sync-engine verwijdert hem op de server (`DELETE /media/{uuid}`). Weigert
     *   de server, dan komt hij terug en zegt de conflictenlijst waarom.
     */
    async remove(clientUuid: string, db: TenantDatabase = requireTenantDb()) {
        const queued = await applyMutation(
            {
                apply: async (database) => {
                    const photo = await database.photos.get(clientUuid);

                    if (!photo) {
                        return;
                    }

                    await assertEditable(database, photo.inspectionUuid);

                    if (!canRemovePhoto(photo)) {
                        throw new PhotoNotRemovableError(clientUuid);
                    }

                    if (photo.state === 'pending' || photo.state === 'failed') {
                        await database.photos.delete(clientUuid);

                        return;
                    }

                    await database.photos.update(clientUuid, {
                        deleted: 1,
                        attempts: 0,
                        nextAttemptAt: 0,
                        lastError: null,
                    });
                },
            },
            db,
        );

        await acknowledgeConflicts(db, [clientUuid]);

        return queued;
    },
};

/**
 * Een foto die al op de server staat, maar niet vanaf dit apparaat is geüpload,
 * heeft geen bekend media-id: de app kan hem dan niet verwijderen.
 */
export function canRemovePhoto(photo: Pick<PhotoRecord, 'state' | 'mediaUuid'>): boolean {
    return photo.state !== 'uploaded' || Boolean(photo.mediaUuid);
}

/** Opnieuw proberen heeft alleen zin zolang de bytes nog op dit apparaat staan. */
export function canRetryPhoto(photo: Pick<PhotoRecord, 'state' | 'blob'>): boolean {
    return photo.state === 'failed' && photo.blob !== null;
}

export class PhotoNotRemovableError extends Error {
    constructor(readonly clientUuid: string) {
        super('Deze foto staat alleen op de server en kan op dit apparaat niet verwijderd worden.');
        this.name = 'PhotoNotRemovableError';
    }
}
